/**
 * useCandidateData Hook
 *
 * Shared hook for fetching candidate data from different sources.
 * Supports: all candidates (directory), workspace tracked.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../../lib/api';
import {
  BaseCandidate,
  CandidateDataSource,
  CandidateFilterState,
  SortState,
} from './types';
import { mapCandidateContactFields } from './candidateContactUtils';
import { normalizeCustomFieldValues } from './candidateCustomFieldUtils';
import { CLIENT_ONLY_SORT_FIELDS } from './candidateSortUtils';

// Backend returns snake_case; frontend uses camelCase
interface RawCandidate {
  id: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  photo_url?: string | null;
  office_sought?: string;
  district?: string | null;
  party_affiliation?: string | null;
  election_year?: number;
  status?: string;
  is_incumbent?: boolean;
  incumbent_legislator_id?: number | null;
  election_type?: string | null;
  primary_email?: string | null;
  email?: string | null;
  primary_phone?: string | null;
  phone?: string | null;
  website?: string | null;
  active_staff_count?: number | string;
  campaign_manager_name?: string | null;
  campaign_manager_email?: string | null;
  treasurer_name?: string | null;
  incumbent_chamber?: string | null;
  incumbent_district?: string | null;
  incumbent_party?: string | null;
  website_url?: string | null;
  social_media?: { platform?: string; handle?: string }[] | null;
  total_funds_raised?: number | string | null;
  seat_incumbent_name?: string | null;
  seat_incumbent_id?: number | null;
  // Tracked candidate fields
  is_priority?: boolean;
  position?: string;
  tracking_notes?: string;
  custom_field_count?: number | string;
  custom_field_values?: unknown;
  candidate_id?: string;
  total_raised?: number | string | null;
  [key: string]: unknown;
}

function normalizeCandidate(raw: RawCandidate): BaseCandidate {
  const contact = mapCandidateContactFields(raw);
  return {
    id: raw.id || raw.candidate_id || '',
    fullName: raw.full_name || `${raw.first_name || ''} ${raw.last_name || ''}`.trim() || 'Unknown',
    firstName: raw.first_name || undefined,
    lastName: raw.last_name || undefined,
    photoUrl: raw.photo_url || null,
    officeSought: raw.office_sought || '',
    district: raw.district || null,
    partyAffiliation: raw.party_affiliation || null,
    electionYear: raw.election_year || new Date().getFullYear(),
    status: raw.status || 'active',
    isIncumbent: raw.is_incumbent || false,
    incumbentLegislatorId: raw.incumbent_legislator_id || null,
    electionType: raw.election_type || null,
    email: raw.primary_email || raw.email || null,
    phone: raw.primary_phone || raw.phone || null,
    website: contact.website,
    socialMedia: contact.socialMedia,
    activeStaffCount: typeof raw.active_staff_count === 'string'
      ? parseInt(raw.active_staff_count, 10) || 0
      : raw.active_staff_count,
    campaignManagerName: raw.campaign_manager_name || null,
    campaignManagerEmail: raw.campaign_manager_email || null,
    treasurerName: raw.treasurer_name || null,
    incumbentChamber: raw.incumbent_chamber || null,
    incumbentDistrict: raw.incumbent_district || null,
    incumbentParty: raw.incumbent_party || null,
    totalFundsRaised: raw.total_funds_raised != null
      ? (typeof raw.total_funds_raised === 'string' ? parseFloat(raw.total_funds_raised) || 0 : raw.total_funds_raised)
      : raw.total_raised != null
        ? (typeof raw.total_raised === 'string' ? parseFloat(raw.total_raised) || 0 : raw.total_raised)
        : null,
    seatIncumbentName: raw.seat_incumbent_name || null,
    seatIncumbentId: raw.seat_incumbent_id || null,
    isPriority: raw.is_priority || false,
    position: raw.position as BaseCandidate['position'] || undefined,
    trackingNotes: raw.tracking_notes || undefined,
    customFieldCount: typeof raw.custom_field_count === 'string'
      ? parseInt(raw.custom_field_count, 10) || 0
      : (raw.custom_field_count as number) || 0,
    customFieldValues: normalizeCustomFieldValues(raw.custom_field_values),
  };
}

interface UseCandidateDataOptions {
  source: CandidateDataSource;
  workspaceId?: string;
  filters?: CandidateFilterState;
  sort?: SortState;
  enabled?: boolean;
  staleTime?: number;
}

interface UseCandidateDataReturn<T extends BaseCandidate> {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  totalCount: number;
  refetch: () => void;
  query: UseQueryResult<T[], Error>;
}

interface UseCandidateMutationsReturn {
  updatePriority: (candidateId: string, isPriority: boolean) => Promise<void>;
  updatePosition: (candidateId: string, position: string) => Promise<void>;
  removeTracking: (candidateId: string) => Promise<void>;
  removeBulkTracking: (candidateIds: string[]) => Promise<void>;
  isUpdating: boolean;
}

export function useCandidateData<T extends BaseCandidate = BaseCandidate>(
  options: UseCandidateDataOptions
): UseCandidateDataReturn<T> {
  const {
    source,
    workspaceId,
    filters,
    sort,
    enabled = true,
    staleTime = 30000,
  } = options;

  const queryKey = getQueryKey(source, workspaceId, filters, sort);

  const fetchFn = async (): Promise<T[]> => {
    switch (source) {
      case 'all':
        return fetchAllCandidates<T>(filters, sort);
      case 'tracked':
        if (!workspaceId) throw new Error('workspaceId required for tracked source');
        return fetchTrackedCandidates<T>(workspaceId);
      case 'report':
        if (!workspaceId) throw new Error('workspaceId required for report source');
        return fetchReportCandidates<T>(workspaceId);
      case 'report-all':
        if (!workspaceId) throw new Error('workspaceId required for report-all source');
        return fetchReportAllCandidates<T>(workspaceId);
      default:
        throw new Error(`Unknown data source: ${source}`);
    }
  };

  const queryResult = useQuery<T[], Error>(queryKey, fetchFn, {
    enabled,
    staleTime,
    keepPreviousData: true,
  });

  return {
    data: queryResult.data || [],
    isLoading: queryResult.isLoading,
    error: queryResult.error,
    totalCount: queryResult.data?.length || 0,
    refetch: queryResult.refetch,
    query: queryResult,
  };
}

export function useTrackedCandidateIds() {
  return useQuery<string[]>(
    ['tracked-candidate-ids'],
    async () => {
      const response = await api.get('/workspace-candidate-tracking');
      if (response.data.success) {
        const raw = response.data.data?.candidateIds;
        return Array.isArray(raw) ? raw.map(String) : [];
      }
      return [];
    },
    { staleTime: 60000 }
  );
}

export function useCandidateMutations(workspaceId: string): UseCandidateMutationsReturn {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries(['workspace-tracked-candidates', workspaceId]);
    queryClient.invalidateQueries(['tracked-candidate-ids']);
  };

  const priorityMutation = useMutation(
    async ({ candidateId, isPriority }: { candidateId: string; isPriority: boolean }) => {
      await api.put(
        `/workspace-candidate-tracking/workspace/${workspaceId}/candidate/${candidateId}/priority`,
        { isPriority }
      );
    },
    { onSuccess: invalidateQueries }
  );

  const positionMutation = useMutation(
    async ({ candidateId, position }: { candidateId: string; position: string }) => {
      await api.put(
        `/workspace-candidate-tracking/workspace/${workspaceId}/candidate/${candidateId}/position`,
        { position }
      );
    },
    { onSuccess: invalidateQueries }
  );

  const removeMutation = useMutation(
    async (candidateId: string) => {
      await api.delete('/workspace-candidate-tracking', {
        data: { candidateId, workspaceIds: [workspaceId] }
      });
    },
    { onSuccess: invalidateQueries }
  );

  const removeBulkMutation = useMutation(
    async (candidateIds: string[]) => {
      const response = await api.post('/workspace-candidate-tracking/bulk', {
        candidateIds,
        workspaceIds: [workspaceId],
        action: 'remove',
      });
      if (!response.data.success) {
        throw new Error('Failed to remove candidate tracking');
      }
      return response.data;
    },
    { onSuccess: invalidateQueries }
  );

  return {
    updatePriority: (candidateId: string, isPriority: boolean) =>
      priorityMutation.mutateAsync({ candidateId, isPriority }),
    updatePosition: (candidateId: string, position: string) =>
      positionMutation.mutateAsync({ candidateId, position }),
    removeTracking: (candidateId: string) =>
      removeMutation.mutateAsync(candidateId),
    removeBulkTracking: (candidateIds: string[]) =>
      removeBulkMutation.mutateAsync(candidateIds),
    isUpdating: priorityMutation.isLoading || positionMutation.isLoading || removeMutation.isLoading || removeBulkMutation.isLoading,
  };
}

// =============================================================================
// Internal fetch functions
// =============================================================================

function getQueryKey(
  source: CandidateDataSource,
  workspaceId?: string,
  filters?: CandidateFilterState,
  sort?: SortState
): (string | object | undefined)[] {
  const apiSort =
    sort && !CLIENT_ONLY_SORT_FIELDS.has(sort.field) ? sort : undefined;

  switch (source) {
    case 'all':
      return ['candidates-all', filters, apiSort];
    case 'tracked':
      return ['workspace-tracked-candidates', workspaceId];
    default:
      return ['candidates', source, workspaceId, filters, sort];
  }
}

async function fetchAllCandidates<T extends BaseCandidate>(
  filters?: CandidateFilterState,
  sort?: SortState
): Promise<T[]> {
  const params: Record<string, string> = {};

  if (filters) {
    if (filters.searchQuery) params.search = filters.searchQuery;
    if (filters.officeSought && filters.officeSought !== 'All') params.office_sought = filters.officeSought;
    if (filters.party && filters.party !== 'All parties') params.party = filters.party;
    if (filters.district && filters.district !== 'All districts') params.district = filters.district;
    if (filters.electionYear && filters.electionYear !== 'All') params.election_year = filters.electionYear;
    if (filters.status && filters.status !== 'All') params.status = filters.status;
  }

  if (sort && !CLIENT_ONLY_SORT_FIELDS.has(sort.field)) {
    params.sortBy = sort.field;
    params.sortDirection = sort.direction;
  }

  const response = await api.get('/candidates', { params });

  if (response.data.success) {
    return (response.data.data as RawCandidate[]).map(normalizeCandidate) as T[];
  }

  throw new Error('Failed to fetch candidates');
}

async function fetchTrackedCandidates<T extends BaseCandidate>(
  workspaceId: string
): Promise<T[]> {
  const response = await api.get(`/workspace-candidate-tracking/workspace/${workspaceId}`);

  if (response.data.success) {
    return (response.data.data.candidates as RawCandidate[]).map(normalizeCandidate) as T[];
  }

  throw new Error('Failed to fetch tracked candidates');
}

async function fetchReportCandidates<T extends BaseCandidate>(
  workspaceId: string
): Promise<T[]> {
  const response = await api.get(`/workspace-reports/${workspaceId}/candidates`);
  if (response.data.success) {
    return (response.data.data as RawCandidate[]).map(normalizeCandidate) as T[];
  }
  throw new Error('Failed to fetch report candidates');
}

async function fetchReportAllCandidates<T extends BaseCandidate>(
  workspaceId: string
): Promise<T[]> {
  const response = await api.get(`/workspace-reports/${workspaceId}/candidates/all`);
  if (response.data.success) {
    return (response.data.data as RawCandidate[]).map(normalizeCandidate) as T[];
  }
  throw new Error('Failed to fetch all candidates for report');
}
